// «Как покупатель нашёл товар» — источник заказа для кабинета продавца
// (колонка таблицы продаж, `orders.discovery_source`).
//
// Источник определяется НЕ в момент нажатия «Оформить», а там, где покупатель
// реально пришёл к товару: в выдаче поиска, в каталоге, в блоке рекомендаций,
// в подборке акции. Между этим моментом и оплатой человек успевает уйти,
// вернуться на следующий день и оформить заказ с главной — если спрашивать
// «откуда ты?» на чекауте, ответом всегда будет «ниоткуда».
//
// Поэтому источник запоминается при переходе на карточку товара и живёт в
// `localStorage` до оформления. Модель атрибуции — last touch: последний
// осмысленный путь к товару и есть тот, который привёл к покупке.
import { createContext, useContext } from "react";

/**
 * Значения ровно те, что принимает колонка `orders.discovery_source`
 * (CHECK в миграции 092). `promo` витрина не проставляет: промокод известен
 * самому заказу, и шлюз берёт его оттуда.
 */
export type DiscoverySource = "search" | "catalog" | "promotion" | "ads" | "recommendations" | "other"
// Лента вдохновения: товар найден в идее (fd-discovery-06).
| "inspiration_feed";
const STORAGE_KEY = "osmix-discovery-source";

/**
 * Срок жизни запомненного источника.
 *
 * 30 дней — обычное окно атрибуции: дольше связь «увидел → купил» перестаёт
 * быть связью, и приписывать заказ месячной давности той давней выдаче поиска
 * значило бы выдавать совпадение за причину.
 */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
type Stored = {
  source: DiscoverySource;
  at: number;
};
const isBrowser = (): boolean => typeof window !== "undefined";

/**
 * Запомнить, откуда покупатель пришёл к товару.
 *
 * Перезаписывает предыдущее значение: last touch. Исключение — `ads`: рекламный
 * переход это канал привлечения, и он не должен стираться тем, что покупатель
 * дальше походил по каталогу внутри сайта.
 */
export const rememberDiscoverySource = (source: DiscoverySource): void => {
  if (!isBrowser()) return;
  try {
    if (source !== "ads" && readStored()?.source === "ads") return;
    const value: Stored = {
      source,
      at: Date.now()
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Хранилище заблокировано — источник просто останется неизвестным,
    // и кабинет честно покажет «—». Ронять витрину из-за этого нельзя.
  }
};
const readStored = (): Stored | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed?.source || typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** Источник для оформляемого заказа. Ничего не запомнили — поля в заказе не будет. */
export const currentDiscoverySource = (): DiscoverySource | undefined => readStored()?.source;

/** Забыть источник — после успешного оформления он относится к прошлому заказу. */
export const clearDiscoverySource = (): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // см. rememberDiscoverySource
  }
};

/**
 * Рекламный переход. Разбирается один раз при загрузке витрины: если в адресе
 * есть метка рекламной кампании, дальше все пути к товару в этой сессии всё
 * равно начались с рекламы.
 */
export const captureAdsLanding = (search: string): void => {
  const params = new URLSearchParams(search);
  const isAds = params.has("gclid") || params.has("yclid") || params.has("utm_campaign") || ["cpc", "ppc", "paid", "display", "banner", "cpm"].includes((params.get("utm_medium") ?? "").toLowerCase());
  if (isAds) rememberDiscoverySource("ads");
};

/**
 * Откуда карточка товара показана прямо сейчас.
 *
 * Контекст, а не проп через всё дерево: `ProductCard` живёт в семи местах
 * витрины (каталог, главная, бутик, подборка, «похожие» на карточке товара), и
 * тащить через них один и тот же проп значило бы править семь файлов ради
 * одного значения. По умолчанию — `other`: карточка вне размеченной области
 * честно не знает, откуда она.
 */
const SourceContext = createContext<DiscoverySource>("other");
export const DiscoverySourceProvider = SourceContext.Provider;
export const useDiscoverySource = () => {
  return useContext(SourceContext);
};